import React from "react";
import {
  Map,
  Marker,
  NavigationControl,
} from "@vietmap/vietmap-gl-js/dist/vietmap-gl.js";
import "@vietmap/vietmap-gl-js/dist/vietmap-gl.css";
import { VIETMAP_STYLE_URL, hasVietmapTileKey } from "services/vietmapService";
import { useLanguage } from "context/LanguageContext";

const DEFAULT_ZOOM = 15;

interface VenueMapPickerProps {
  latitude: number;
  longitude: number;
  onPick: (lat: number, lng: number) => void;
}

const VenueMapPicker = ({
  latitude,
  longitude,
  onPick,
}: VenueMapPickerProps): JSX.Element => {
  const { t } = useLanguage();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const mapRef = React.useRef<Map | null>(null);
  const markerRef = React.useRef<Marker | null>(null);
  const onPickRef = React.useRef(onPick);

  React.useEffect(() => {
    onPickRef.current = onPick;
  }, [onPick]);

  React.useEffect(() => {
    if (!containerRef.current || !VIETMAP_STYLE_URL || mapRef.current)
      return undefined;

    const map = new Map({
      container: containerRef.current,
      style: VIETMAP_STYLE_URL,
      center: [longitude, latitude],
      zoom: DEFAULT_ZOOM,
      attributionControl: false,
    });

    const marker = new Marker({ color: "#ff6b00" })
      .setLngLat([longitude, latitude])
      .addTo(map);

    map.addControl(new NavigationControl(), "top-right");
    map.on("click", (event: { lngLat: { lat: number; lng: number } }) => {
      onPickRef.current(
        Number(event.lngLat.lat.toFixed(6)),
        Number(event.lngLat.lng.toFixed(6))
      );
    });

    mapRef.current = map;
    markerRef.current = marker;

    return () => {
      marker.remove();
      map.remove();
      markerRef.current = null;
      mapRef.current = null;
    };
  }, [latitude, longitude]);

  React.useEffect(() => {
    markerRef.current?.setLngLat([longitude, latitude]);
    mapRef.current?.setCenter([longitude, latitude]);
  }, [latitude, longitude]);

  if (!hasVietmapTileKey) {
    return (
      <div className="rounded-xl border border-dashed border-line bg-gray-50 p-4 text-xs text-gray-600 dark:bg-surface/50 dark:text-gray-300">
        {t("venue.missingTileKey")}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-line">
      <div ref={containerRef} className="ui-map-canvas" />
    </div>
  );
};

export default VenueMapPicker;
